import { useAccount } from 'wagmi'
import { api as blinkoApi } from '../api'
import { atom, useAtom, useAtomValue } from 'jotai'
import { AchievementMetadata, ACHIEVEMENTS } from '../assets/new-achievements/achievements'
import { ReactNode, useEffect } from 'react'
import { atomWalletModal } from '~/blinko/modals/wallet.modal'
import { useNotification } from './NotificationHandler'
import { isAddressEqual } from 'viem'
import { getLeauge } from '../assets/leagues/leagues'

import useSWR, { useSWRConfig } from 'swr'
import { profileCache } from '~/blinko/components/ProfileSlot'

const METADATA_SWR_KEYS = {
  ACHIEVEMENTS: 'METADATA_ACHIEVEMENTS',
  LEADERBOARD: 'METADATA_LEADERBOARD',
}

const atomAchievementsMetadata = atom<AchievementMetadata | null>(null)
const atomLeaderboardMetadata = atom<ReturnType<typeof getLeauge> | null>(null)
const atomLeaderboard = atom<{
  total: Awaited<ReturnType<typeof blinkoApi.getLeaderboard>>;
  daily: Awaited<ReturnType<typeof blinkoApi.getDailyLeaderboard>>;
} | null>(null)
const atomDailyLeaderboardMetadata = atom<ReturnType<typeof getLeauge> | null>(null)

export const useMetadata = () => {
  const { address } = useAccount()
  const { addNotification } = useNotification()
  const isSigned = useAtomValue(atomWalletModal.isSigned)
  const { mutate } = useSWRConfig()

  const [achievementsMetadata, setAchievementsMetadata] = useAtom(atomAchievementsMetadata)
  const [leaderboardMetadata, setLeaderboardMetadata] = useAtom(atomLeaderboardMetadata)
  const [dailyLeaderboardMetadata, setDailyLeaderboardMetadata] = useAtom(atomDailyLeaderboardMetadata)

  const [leaderboard, setLeaderboard] = useAtom(atomLeaderboard)

  const fetchAchievementsMetadata = async () => {
    if (!address || !isSigned) {
      setAchievementsMetadata(null)
      return
    }

    const profile = await blinkoApi.getProfile(address)
    const stats = profile.stats!

    const honeyEarned = Number(profile.totalHoney ?? '0')
    const totalGamesPlayed = stats.totalGamesPlayed ?? 0
    const referralsCount = stats.referralsCount ?? 0
    const highestMultiplier = stats.highestMultiplier ?? 0
    const bonusGamesReached = stats.bonusGamesReached ?? 0
    const highestBonusLevel = stats.highestBonusLevel ?? 0
    const maxDailyStreak = stats.maxDailyStreak ?? 0
    const currentDailyStreak = stats.currentDailyStreak ?? 0

    const newAchievementsMetadata: AchievementMetadata = {
      totalGamesPlayed,
      referralsCount,
      highestMultiplier,
      bonusGamesReached,
      highestBonusLevel,
      maxDailyStreak,
      currentDailyStreak,
      honeyEarned,
    }

    const newCompleted = ACHIEVEMENTS.filter((x) => x.getProgress(newAchievementsMetadata) >= 1)

    if (achievementsMetadata) {
      const prevCompleted = ACHIEVEMENTS.filter((x) => x.getProgress(achievementsMetadata) >= 1)
      const completed = newCompleted.filter((x) => !prevCompleted.includes(x))
      completed.forEach((x) => {
        addNotification({
          title: 'Achievement Unlocked',
          description: x.title,
          image: x.image,
        })
      })
    }

    profileCache.set(address, profile)
    setAchievementsMetadata(newAchievementsMetadata)
  }

  const fetchLeaderboardMetadata = async () => {
    const [leaderboardRes, dailyLeaderboardRes] = await Promise.all([
      blinkoApi.getLeaderboard(address),
      blinkoApi.getDailyLeaderboard(address),
    ])

    const totalIdxInTop = address ? leaderboardRes.leaderboard.findIndex(x => isAddressEqual(x.address, address)) : -1
    const totalRank = leaderboardRes.myRank?.rank ?? (totalIdxInTop !== -1 ? leaderboardRes.leaderboard[totalIdxInTop].rank : -1)
    const dailyIdxInTop = address ? dailyLeaderboardRes.leaderboard.findIndex(x => isAddressEqual(x.address, address)) : -1
    const dailyRank = dailyLeaderboardRes.myRank?.rank ?? (dailyIdxInTop !== -1 ? dailyLeaderboardRes.leaderboard[dailyIdxInTop].rank : -1)

    setLeaderboard({ total: leaderboardRes, daily: dailyLeaderboardRes })

    if (totalRank === -1) {
      setLeaderboardMetadata(null)
    } else {
      const newLeaderboardMetadata = getLeauge(totalRank)

      if (leaderboardMetadata) {
        if (leaderboardMetadata.league === 'Diamond' && newLeaderboardMetadata.league === 'Diamond') {
          if (leaderboardMetadata.rank > newLeaderboardMetadata.rank) {
            addNotification({
              title: 'Leaderboard Update',
              description: 'Up from ' + leaderboardMetadata.ordinal + ' to ' + newLeaderboardMetadata.ordinal + '!',
              image: newLeaderboardMetadata.image,
            })
          }
        } else {
          if (leaderboardMetadata.leagueIndex > newLeaderboardMetadata.leagueIndex) {
            addNotification({
              title: 'Leaderboard Update',
              description: 'Promoted to ' + newLeaderboardMetadata.league + '!',
              image: newLeaderboardMetadata.image,
            })
          }
        }
      }
  
      setLeaderboardMetadata(newLeaderboardMetadata)
    }

    // daily
    if (dailyRank === -1) {
      setDailyLeaderboardMetadata(null)
    } else {
      const newLeaderboardMetadata = getLeauge(dailyRank)

      if (dailyLeaderboardMetadata) {
        if (dailyLeaderboardMetadata.league === 'Diamond' && newLeaderboardMetadata.league === 'Diamond') {
          if (dailyLeaderboardMetadata.rank > newLeaderboardMetadata.rank) {
            addNotification({
              title: 'Daily Leaderboard Update',
              description: 'Up from ' + dailyLeaderboardMetadata.ordinal + ' to ' + newLeaderboardMetadata.ordinal + '!',
              image: newLeaderboardMetadata.image,
            })
          }
        }
      }
  
      setDailyLeaderboardMetadata(newLeaderboardMetadata)
    }
  }

  const mutateAchievements = () => {
    mutate(METADATA_SWR_KEYS.ACHIEVEMENTS)
  }

  const mutateLeaderboard = () => {
    mutate(METADATA_SWR_KEYS.LEADERBOARD)
  }

  return {
    fetchAchievementsMetadata,
    fetchLeaderboardMetadata,
    achievementsMetadata,
    leaderboardMetadata,
    leaderboard,
    mutateAchievements,
    mutateLeaderboard,
  }
}

export const MetadataSlot = ({ children }: { children: (metadata: ReturnType<typeof useMetadata>) => ReactNode }) => {
  const { fetchAchievementsMetadata, fetchLeaderboardMetadata, achievementsMetadata, leaderboardMetadata, leaderboard, mutateAchievements, mutateLeaderboard } = useMetadata()

  return children({
    fetchAchievementsMetadata,
    fetchLeaderboardMetadata,
    achievementsMetadata,
    leaderboardMetadata,
    leaderboard,
    mutateAchievements,
    mutateLeaderboard,
  })
}

export const MetadataHandler = () => {
  const { address } = useAccount()
  const { mutate } = useSWRConfig()
  const { fetchAchievementsMetadata, fetchLeaderboardMetadata } = useMetadata()

  const isSigned = useAtomValue(atomWalletModal.isSigned)

  useSWR(METADATA_SWR_KEYS.ACHIEVEMENTS, fetchAchievementsMetadata, {
    revalidateOnMount: false,
    revalidateOnFocus: false,
  })

  useSWR(METADATA_SWR_KEYS.LEADERBOARD, fetchLeaderboardMetadata, {
    revalidateOnFocus: false,
    revalidateOnMount: false,
  })

  useEffect(() => {
    mutate(METADATA_SWR_KEYS.ACHIEVEMENTS)
  }, [address, isSigned])

  useEffect(() => {
    mutate(METADATA_SWR_KEYS.LEADERBOARD)
  }, [address])

  return <></>
}
